/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable max-len */
/* eslint-disable default-case */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';
import NavBar from '../sharedComponents/NavBar';
import '../../styles/ChatScreen.css';
import data from '../../assets/conversation_mockup.json';
import Workspaces from '../sharedComponents/Workspaces.tsx';
import Grid from '@mui/material/Grid';
import { useChatContext } from '../../contexts/ChatContext';
import { CanvasPanelProvider } from '../../contexts/CanvasPanelContext';
import { getAllUsers, getAllAgents, getInformation, getUser } from '../../resources/api.resource.ts';
import store from '../../utils/store.ts';
import { transformUser, transformAgent, buildUsersMap } from '../../utils/userTransformers';
// import { fetchChatHistory } from '../../hooks/fetchChatHistory';

const ChatScreen = ({ logedInUserMail, logedInUserName }) => {
  const [selectedNavBar, setSelectedNavBar] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedSearchType, setSelectedSearchType] = useState('Hybrid Search');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [newChatFlag, setNewChatFlag] = useState(() => {
    const param = queryParams.get("new_chat");
    return param === "true" || param === null;
  });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hasMessages, setHasMessages] = useState(false);
  const { setGroupedChatHistory, setChatDetails } = useChatContext();
  const { collaboration_id } = useParams();
  const [allUsersMap, setAllUsersMap] = useState({});
  const [collaborations, setCollaborations] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [computedCollaborationId, setComputedCollaborationId] = useState(null);
  const mainRef = useRef(null);
  const isFirstRender = useRef(true);

  // The NavBar tabs are this app's real top-level navigation (the route never
  // changes), so a tab switch is what should move focus into <main> — otherwise
  // a keyboard user stays parked on the tab they just left and has to tab back
  // through the whole nav. Skipped on first render: moving focus on initial
  // page load is a WCAG 3.2.1 hazard, not a fix.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    mainRef.current?.focus();
  }, [selectedNavBar]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        // Fetch current user, users and agents in parallel
        const [user, rawUsers, rawAgents] = await Promise.all([
          getUser(),
          getAllUsers(),
          getAllAgents().catch(err => {
            console.warn('Failed to fetch agents, continuing with users only:', err);
            return [];
          })
        ]);

        // Transform to internal format
        const usersWithType = rawUsers.map(transformUser);
        const agentsWithType = rawAgents.map(transformAgent);
        const allUsers = [...usersWithType, ...agentsWithType];

        // Create map for lookups
        const map = buildUsersMap(allUsers);
        setAllUsersMap(map);
        setCurrentUser(user);

        // Fetch collaborations
        const collabs = await getInformation();
        setCollaborations(collabs);

        // Dispatch to Redux
        store.dispatch({
          type: 'set',
          collaborations: collabs,
          allUsers: allUsers
        });

      } catch (err) {
        console.error("Error fetching data in ChatScreen:", err);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, []);

  // Compute collaboration ID for Chat view (navbar 0)
  useEffect(() => {

    if (newChatFlag && !collaboration_id) {
      setComputedCollaborationId(null);
      return;
    }

    if (selectedNavBar === 0 && !collaboration_id && collaborations.length > 0 && Object.keys(allUsersMap).length > 0) {
      // Find first DM with an agent
      const dmCollabs = collaborations.filter(c => c.collaborationType === "dm");

      for (const dm of dmCollabs) {
        // Check if any memberUid is an agent
        const hasAgent = dm.memberUids?.some(uid =>
          allUsersMap[uid.toLowerCase()]?.userType === 'agent'
        );

        if (hasAgent) {
          setComputedCollaborationId(dm.collab_instances[0] ? dm.collab_instances[0].id : dm.collaborationId);
          return;
        }
      }

      // No agent DM found
      setComputedCollaborationId(null);
    } else if (selectedNavBar === 1 || collaboration_id) {
      // Reset for Collaborations view or when URL param exists
      setComputedCollaborationId(null);
    }
  }, [selectedNavBar, collaboration_id, collaborations, allUsersMap, newChatFlag]);

  return (
    <div className="chat-container chat-route">
      {/* First focusable element in the DOM, so one Tab from page load offers a
          jump past the nav into the conversation (WCAG 2.4.1). */}
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <Grid container sx={{ width: '100%' }} spacing={0}>
        <Grid item size="xs">
          <NavBar changeSelectedNavBar={setSelectedNavBar} logedInUserMail={logedInUserMail} logedInUserName={logedInUserName} currentUser={currentUser} selectedNavBar={selectedNavBar} />
        </Grid>
        <Grid item size="grow">
          {/* tabIndex={-1} makes <main> a programmatic focus target for the skip
              link and the tab-change effect without adding it to the tab order. */}
          <main id="main-content" ref={mainRef} tabIndex={-1} className="chat-body">
            {/* The app has no visible page title, so the document needs one
                heading to anchor the outline the section/conversation h2s hang
                off (WCAG 1.3.1). */}
            <h1 className="sr-only">
              {selectedNavBar === 1 ? 'Collaborations' : 'Chat'}
            </h1>
            {isLoadingData ? (
              <div role="status" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                width: '100%',
                backgroundColor: '#f5f5f5'
              }}>
                <div style={{
                  textAlign: 'center',
                  padding: '40px'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    border: '4px solid #e0e0e0',
                    borderTop: '4px solid #0857c3',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 20px'
                  }} />
                  <p style={{
                    color: '#555',
                    fontSize: '1rem',
                    fontFamily: "var(--app-font-family, 'Proxima Nova')",
                    margin: 0
                  }}>
                    Loading...
                  </p>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              </div>
            ) : (
              <CanvasPanelProvider>
                <Workspaces
                  fetchedMessages={data}
                  initialCollaborationId={collaboration_id || computedCollaborationId}
                  showLeftPanel={selectedNavBar === 1}
                  allUsersMap={allUsersMap}
                  collaborations={collaborations}
                  currentUser={currentUser}
                  newChatFlag={newChatFlag}
                />
              </CanvasPanelProvider>
            )}
          </main>
        </Grid>
      </Grid>

      {/* {!hasMessages && (
        <footer className="chat-footer">
          These are AI generated content, check before using
        </footer>
      )} */}
    </div>
  );
};

ChatScreen.propTypes = {
  logedInUserMail: PropTypes.string.isRequired,
};

export default ChatScreen;
